import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Hook utility per kanban drag-and-drop con HTML5 native API.
 *
 * Versione "Trello-like" con drop preciso tra le card:
 *
 *   const dnd = useKanbanDnd<TaskStatus>((id, newStatus, position) =>
 *     mutate({ id, status: newStatus, position })
 *   );
 *
 *   <div {...dnd.columnProps(status)}>
 *     <div {...dnd.dropZoneProps(status, 0)} className={dnd.isOverZone(status, 0) ? 'h-8' : 'h-1'} />
 *     {cards.map((c, idx) => (
 *       <Fragment key={c.id}>
 *         <div {...dnd.cardProps(c.id, c.status)}>...</div>
 *         <div {...dnd.dropZoneProps(status, idx + 1)} ... />
 *       </Fragment>
 *     ))}
 *   </div>
 */
export function useKanbanDnd<TStatus extends string>(
  onMove: (itemId: string, newStatus: TStatus, position: number) => void,
) {
  const [dragging, setDragging] = useState<{ id: string; from: TStatus } | null>(null);
  const [overCol, setOverCol] = useState<TStatus | null>(null);
  const [overZone, setOverZone] = useState<{ status: TStatus; position: number } | null>(null);

  // Ref per leggere stato corrente dentro listener globali senza stale closure
  const draggingRef = useRef(dragging);
  draggingRef.current = dragging;

  const reset = useCallback(() => {
    setDragging(null);
    setOverCol(null);
    setOverZone(null);
  }, []);

  useEffect(() => {
    if (dragging) document.body.classList.add('dnd-dragging');
    else document.body.classList.remove('dnd-dragging');
    return () => { document.body.classList.remove('dnd-dragging'); };
  }, [dragging]);

  // Cleanup robusto: ESC, blur finestra, drop fuori da qualunque target valido
  useEffect(() => {
    if (!dragging) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') reset(); };
    const onWindowDrop = () => { reset(); };
    const onWindowBlur = () => { reset(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('drop', onWindowDrop);
    window.addEventListener('dragend', onWindowDrop);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('drop', onWindowDrop);
      window.removeEventListener('dragend', onWindowDrop);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [dragging, reset]);

  const cardProps = useCallback((itemId: string, fromStatus: TStatus) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData('text/plain', itemId);
      e.dataTransfer.effectAllowed = 'move';
      setDragging({ id: itemId, from: fromStatus });
    },
    onDragEnd: reset,
    style: dragging?.id === itemId ? { opacity: 0.35 } : undefined,
  }), [dragging, reset]);

  const columnProps = useCallback((status: TStatus) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (overCol !== status) setOverCol(status);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      if (overCol === status) setOverCol(null);
      // Anche la zona è invalidata uscendo dalla colonna
      if (overZone?.status === status) setOverZone(null);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const id = e.dataTransfer.getData('text/plain') || draggingRef.current?.id;
      if (!id) { reset(); return; }
      // Posizione coerente: se la zona attiva è in QUESTA colonna usala; altrimenti append.
      // MAX_SAFE_INTEGER è accettato dal backend (z.int + clamp position >= siblings.length).
      const rawPos = overZone?.status === status ? overZone.position : Number.MAX_SAFE_INTEGER;
      const targetPosition = Number.isFinite(rawPos) && rawPos >= 0 ? Math.floor(rawPos) : Number.MAX_SAFE_INTEGER;
      reset();
      onMove(id, status, targetPosition);
    },
  }), [dragging, onMove, overCol, overZone, reset]);

  const dropZoneProps = useCallback((status: TStatus, position: number) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      const cur = overZone;
      if (!cur || cur.status !== status || cur.position !== position) {
        setOverZone({ status, position });
      }
      if (overCol !== status) setOverCol(status);
    },
    onDrop: (e: React.DragEvent) => {
      // Drop diretto su una zona specifica: garantisce che la position usata sia QUESTA
      e.preventDefault();
      e.stopPropagation();
      const id = e.dataTransfer.getData('text/plain') || draggingRef.current?.id;
      if (!id) { reset(); return; }
      reset();
      onMove(id, status, position);
    },
  }), [overZone, overCol, onMove, reset]);

  const isOverColumn = useCallback((status: TStatus) => overCol === status, [overCol]);
  const isOverZone = useCallback((status: TStatus, position: number) =>
    overZone?.status === status && overZone.position === position, [overZone]);
  const isDragging = useCallback((itemId: string) => dragging?.id === itemId, [dragging]);
  const isAnyDragging = useCallback(() => !!dragging, [dragging]);

  return { cardProps, columnProps, dropZoneProps, isOverColumn, isOverZone, isDragging, isAnyDragging };
}
